export interface ChatParagraphBlock {
  type: 'paragraph';
  text: string;
}

export interface ChatListBlock {
  type: 'unordered-list' | 'ordered-list';
  items: string[];
}

export interface ChatTableBlock {
  type: 'table';
  headers: string[];
  rows: string[][];
}

export type ChatMessageBlock =
  | ChatParagraphBlock
  | ChatListBlock
  | ChatTableBlock;

const TABLE_SEPARATOR_PATTERN =
  /^\s*\|?(?:\s*:?-{3,}:?\s*\|)+\s*:?-{3,}:?\s*\|?\s*$/;
const UNORDERED_LIST_PATTERN = /^\s*[-*]\s+/;
const ORDERED_LIST_PATTERN = /^\s*\d+\.\s+/;

const normalizeInlineText = (value: string): string =>
  value
    .replace(/\s+/g, ' ')
    .replace(/\s+([,.;:!?])/g, '$1')
    .trim();

const parseTableRow = (line: string): string[] => {
  const normalized = line.trim().replace(/^\|/, '').replace(/\|$/, '');
  return normalized.split('|').map(normalizeInlineText);
};

const removeEmptyTableColumns = (
  headers: string[],
  rows: string[][],
): {headers: string[]; rows: string[][]} => {
  const columnCount = Math.max(
    headers.length,
    ...rows.map(row => row.length),
    0,
  );
  const normalizedHeaders = Array.from(
    {length: columnCount},
    (_, index) => headers[index] || '',
  );
  const normalizedRows = rows.map(row =>
    Array.from({length: columnCount}, (_, index) => row[index] || ''),
  );
  const columnIndexesToKeep = normalizedHeaders.reduce<number[]>(
    (indexes, header, index) => {
      const hasCellValue = normalizedRows.some(row => row[index] !== '');
      if (header !== '' || hasCellValue) {
        indexes.push(index);
      }

      return indexes;
    },
    [],
  );

  return {
    headers: columnIndexesToKeep.map(index => normalizedHeaders[index]),
    rows: normalizedRows.map(row =>
      columnIndexesToKeep.map(index => row[index]),
    ),
  };
};

const shouldHideIndexColumn = (
  headers: string[],
  rows: string[][],
): boolean => {
  if (headers[0] !== '#') {
    return false;
  }

  return rows.every((row, index) => row[0] === String(index + 1));
};

const isTableHeader = (line: string, nextLine: string | undefined): boolean =>
  Boolean(
    line?.includes('|') && nextLine && TABLE_SEPARATOR_PATTERN.test(nextLine),
  );

const isTableRow = (line: string): boolean => {
  const trimmed = line.trim();
  return trimmed.includes('|') && !TABLE_SEPARATOR_PATTERN.test(trimmed);
};

const isUnorderedListItem = (line: string): boolean =>
  UNORDERED_LIST_PATTERN.test(line);

const isOrderedListItem = (line: string): boolean =>
  ORDERED_LIST_PATTERN.test(line);

export const parseChatMessageContent = (text: string): ChatMessageBlock[] => {
  const normalizedText = text.replace(/\r\n/g, '\n').trim();
  if (!normalizedText) {
    return [];
  }

  const lines = normalizedText.split('\n');
  const blocks: ChatMessageBlock[] = [];
  let index = 0;

  while (index < lines.length) {
    const line = lines[index];
    const trimmedLine = line.trim();

    if (!trimmedLine) {
      index += 1;
      continue;
    }

    if (isTableHeader(line, lines[index + 1])) {
      const rawHeaders = parseTableRow(line);
      const rawRows: string[][] = [];
      index += 2;

      while (index < lines.length && isTableRow(lines[index])) {
        rawRows.push(parseTableRow(lines[index]));
        index += 1;
      }

      const normalizedTable = removeEmptyTableColumns(rawHeaders, rawRows);
      const hideIndexColumn = shouldHideIndexColumn(
        normalizedTable.headers,
        normalizedTable.rows,
      );
      const headers = hideIndexColumn
        ? normalizedTable.headers.slice(1)
        : normalizedTable.headers;
      const rows = hideIndexColumn
        ? normalizedTable.rows.map(row => row.slice(1))
        : normalizedTable.rows;

      blocks.push({
        type: 'table',
        headers,
        rows,
      });
      continue;
    }

    if (isUnorderedListItem(line)) {
      const items: string[] = [];

      while (index < lines.length && isUnorderedListItem(lines[index])) {
        items.push(
          normalizeInlineText(
            lines[index].replace(UNORDERED_LIST_PATTERN, '').trim(),
          ),
        );
        index += 1;
      }

      blocks.push({
        type: 'unordered-list',
        items,
      });
      continue;
    }

    if (isOrderedListItem(line)) {
      const items: string[] = [];

      while (index < lines.length && isOrderedListItem(lines[index])) {
        items.push(
          normalizeInlineText(
            lines[index].replace(ORDERED_LIST_PATTERN, '').trim(),
          ),
        );
        index += 1;
      }

      blocks.push({
        type: 'ordered-list',
        items,
      });
      continue;
    }

    const paragraphLines: string[] = [];

    while (index < lines.length) {
      const currentLine = lines[index];
      const currentTrimmedLine = currentLine.trim();

      if (
        !currentTrimmedLine ||
        isTableHeader(currentLine, lines[index + 1]) ||
        isUnorderedListItem(currentLine) ||
        isOrderedListItem(currentLine)
      ) {
        break;
      }

      paragraphLines.push(normalizeInlineText(currentTrimmedLine));
      index += 1;
    }

    if (paragraphLines.length > 0) {
      blocks.push({
        type: 'paragraph',
        text: paragraphLines.join('\n'),
      });
      continue;
    }

    index += 1;
  }

  return blocks;
};
